import React, { useEffect, useState } from "react";
import BrandLogo from "./common/BrandLogo";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  Users, Boxes, Home, Contact, Building2, ListChecks,
  UsersRound, ShieldCheck, Settings, LogOut, ArrowLeft, Sparkles,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * EstateOS CRM shell — Bitrix-style layout.
 *   • Fixed left sidebar with modules (Müşteri Adayları active, others "Yakında")
 *   • Right content area where routes render (leads kanban, users, roles, …)
 *   • Top bar with breadcrumb + current user identity + logout
 *
 * Data:
 *   - fetches /api/estateos/me on mount and shares it via <Outlet context>
 */

const NAV_MAIN = [
  { key: "leads",   label: "Müşteri Adayları", to: "/estateos/crm/leads",   icon: Users,       enabled: true  },
  { key: "deals",   label: "Anlaşmalar",       to: "/estateos/crm/deals",   icon: Boxes,       enabled: false },
  { key: "clients", label: "Müşteriler",       to: "/estateos/crm/clients", icon: Contact,     enabled: false },
  { key: "companies", label: "Şirketler",      to: "/estateos/crm/companies", icon: Building2, enabled: false },
  { key: "tasks",   label: "Görevler",         to: "/estateos/crm/tasks",   icon: ListChecks,  enabled: false },
];

const NAV_ADMIN = [
  { key: "users", label: "Çalışanlar",         to: "/estateos/users",  icon: UsersRound,  enabled: true },
  { key: "roles", label: "Roller & İzinler",   to: "/estateos/roles",  icon: ShieldCheck, enabled: true },
  { key: "settings", label: "Ayarlar",         to: "/estateos/settings", icon: Settings,  enabled: false },
];

const NavItem = ({ item }) => {
  const Icon = item.icon;
  if (!item.enabled) {
    return (
      <div
        className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/40 cursor-not-allowed select-none"
        data-testid={`crm-nav-${item.key}`}
      >
        <Icon size={16} strokeWidth={1.5} />
        <span className="flex-1">{item.label}</span>
        <span className="text-[0.55rem] tracking-[0.24em] uppercase text-white/30 border border-white/10 px-1.5 py-0.5">
          Yakında
        </span>
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-colors ${
          isActive
            ? "bg-white/10 text-white"
            : "text-white/70 hover:bg-white/5 hover:text-white"
        }`
      }
      data-testid={`crm-nav-${item.key}`}
    >
      <Icon size={16} strokeWidth={1.5} />
      <span className="flex-1">{item.label}</span>
    </NavLink>
  );
};

const EstateCRMLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/estateos/me")
      .then((r) => setMe(r.data))
      .catch(() => setMe({ permissions: {}, name: user?.name || "" }));
  }, [user]);

  const handleLogout = async () => {
    try { await logout(); } catch { /* noop */ }
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-brand-light" data-testid="estateos-crm-layout">
      {/* Left sidebar */}
      <aside
        className="w-64 flex-shrink-0 flex flex-col text-white"
        style={{ background: "linear-gradient(180deg, #101F4A 0%, #22346B 100%)" }}
      >
        <div className="px-5 py-6 border-b border-white/10">
          <Link to="/estateos" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-[0.62rem] tracking-[0.28em] uppercase">
            <ArrowLeft size={12} strokeWidth={1.5} /> EstateOS
          </Link>
          <div className="mt-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <Sparkles size={14} strokeWidth={1.5} className="text-white" />
            </span>
            <div>
              <div className="text-[0.6rem] tracking-[0.32em] uppercase text-white/60">Suite</div>
              <div className="font-serif text-xl leading-tight">CRM</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <div className="px-3 pb-2 text-[0.55rem] tracking-[0.32em] uppercase text-white/40 font-medium">
            Satış Modülleri
          </div>
          {NAV_MAIN.map((n) => <NavItem key={n.key} item={n} />)}

          <div className="mt-6 px-3 pb-2 text-[0.55rem] tracking-[0.32em] uppercase text-white/40 font-medium">
            Yönetim
          </div>
          {NAV_ADMIN.map((n) => <NavItem key={n.key} item={n} />)}
        </nav>

        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-[0.6rem] tracking-[0.24em] uppercase text-white/50">Oturum</div>
          <div className="text-xs text-white/90 mt-1 truncate">{me?.email || user?.email}</div>
          <div className="text-[0.62rem] tracking-[0.22em] uppercase text-brand-primary-bright mt-1">
            {me?.role_name || "—"}
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/15 hover:border-white/40 text-[0.66rem] tracking-[0.24em] uppercase text-white transition-colors"
            data-testid="crm-logout"
          >
            <LogOut size={12} strokeWidth={1.5} /> Çıkış
          </button>
        </div>
      </aside>

      {/* Right main area */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-black/5 px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo place="header" max={44} />
            <span className="hidden md:inline-flex items-center gap-2 px-3 py-1 bg-brand-navy/5 text-[0.6rem] tracking-[0.28em] uppercase text-brand-navy font-medium">
              EstateOS CRM
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-brand-medium">
            <span className="hidden md:inline">{me?.name || user?.name}</span>
            <Link
              to="/"
              className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium hover:text-brand-navy"
              data-testid="crm-back-to-site"
            >
              Ana Site
            </Link>
          </div>
        </header>
        <div className="flex-1 min-h-0">
          <Outlet context={{ me }} />
        </div>
      </main>
    </div>
  );
};

export default EstateCRMLayout;
